import { useEffect, useState } from "react";

import { format, formatDistanceToNow } from "date-fns";

import {
  HiArrowLeft,
  HiArrowRight,
  HiChevronUp,
  HiOutlineExclamation,
  HiOutlineExclamationCircle,
} from "react-icons/hi";

import useData from "../src/useData";
import useTenant from "../src/useTenant";

import { EnvData } from "../src/types";

import Button from "./Button";
import ProductionOnlySettings from "./ProductionOnlySettings";
import { Disclosure } from "@headlessui/react";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

// Source: https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
function formatBytes(bytes: number, decimals: number = 2) {
  if (!+bytes) return "Not available";

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

interface FallbackEnvironment {
  environment_pub_id: string;
  current_snapshot_timestamp: string;
  current_snapshot_size_bytes: number;
  clients: {
    client_id: string;
    client_version: string;
    snapshot_timestamp: string;
    last_seen_at: string;
  }[];
}

const FallbackClientHealthBadge: React.FC<{
  snapshot_timestamp: string;
  last_seen_at: string;
}> = ({ snapshot_timestamp, last_seen_at }) => {
  const nowEpochMinutes = Date.now() / 1000 / 60;
  const snapshotEpochMinutes = Date.parse(snapshot_timestamp) / 1000 / 60;
  const lastSeenEpochMinutes = Date.parse(last_seen_at) / 1000 / 60;

  let message: string;
  let badgeColor: string;
  let textColor: string;

  if (nowEpochMinutes - lastSeenEpochMinutes > 30) {
    badgeColor = "bg-gray-200";
    textColor = "";
    message = "Disconnected";
  } else if (nowEpochMinutes - snapshotEpochMinutes > 30) {
    badgeColor = "bg-yellow-300";
    textColor = "";
    message = "Copy of rules and data is more than 30 minutes old";
  } else if (nowEpochMinutes - lastSeenEpochMinutes > 5) {
    badgeColor = "bg-yellow-300";
    textColor = "";
    message = "Last seen more than 5 minutes ago";
  } else {
    badgeColor = "bg-green-600";
    textColor = "text-white";
    message = `Last seen ${formatDistanceToNow(new Date(last_seen_at))} ago`;
  }

  return (
    <div
      className={classNames(
        badgeColor,
        textColor,
        "px-2 py-1 rounded font-bold uppercase text-xs w-full"
      )}
    >
      {message}
    </div>
  );
};

const FallbackManagementEnvironment: React.FC<{
  env: EnvData;
  fallback: undefined | FallbackEnvironment;
  className: string;
}> = ({ env, fallback, className }) => {
  const [startIdx, setStartIdx] = useState(0);

  const CLIENTS_PER_PAGE = 10;
  const nowEpochMinutes = Date.now() / 1000 / 60;
  const currentSnapshotEpochMinutes = fallback
    ? Date.parse(fallback.current_snapshot_timestamp) / 1000 / 60
    : 0;

  const connectedClients = fallback?.clients.reduce((acc, c) => {
    const lastSeenEpochMinutes = Date.parse(c.last_seen_at) / 1000 / 60;

    if (nowEpochMinutes - lastSeenEpochMinutes <= 30) {
      acc = acc + 1;
    }

    return acc;
  }, 0);

  return (
    <div className={classNames(className)}>
      <Disclosure defaultOpen={true}>
        {({ open }) => (
          <>
            <div className="flex justify-between">
              <div className="font-normal text-lg text-gray-600">
                Environment: <b className="text-black">{env.name}</b>
              </div>
              {fallback && fallback.clients.length > 0 && (
                <Disclosure.Button>
                  <Button
                    icon={
                      <HiChevronUp
                        className={open ? "" : "rotate-90 transform"}
                      />
                    }
                    xsmall
                    className="rounded"
                  >
                    {open ? "Hide" : "Show"} connected clients
                  </Button>
                </Disclosure.Button>
              )}
            </div>

            {fallback ? (
              <div className="flex">
                <div className="text-gray-500 text-sm">
                  Most recent copy of rules and data:{" "}
                  {format(
                    new Date(fallback?.current_snapshot_timestamp),
                    "yyyy-MM-dd h:mm:ssaaa"
                  )}{" "}
                  (
                  {formatDistanceToNow(
                    new Date(fallback?.current_snapshot_timestamp)
                  )}{" "}
                  ago)
                </div>
                {nowEpochMinutes - currentSnapshotEpochMinutes > 30 && (
                  <div className="relative">
                    <div className="pl-2 items-center text-yellow-400 peer">
                      <HiOutlineExclamation />
                    </div>
                    <div className="hidden peer-hover:block absolute w-96 z-10 bg-white shadow-md rounded-md p-4 text-gray-700 text-sm text-left">
                      The most recent copy of rules and data is over 30 minutes
                      old. We are investigating. Please reach out on Slack if
                      you have any questions.
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex">
                <div className="text-gray-500 text-sm">
                  Copy of rules and data is not available for environment
                </div>
                <div className="relative">
                  <div className="pl-2 items-center text-blue-400 peer">
                    <HiOutlineExclamationCircle />
                  </div>
                  <div className="hidden peer-hover:block absolute w-96 z-10 bg-white shadow-md rounded-md p-4 text-gray-700 text-sm text-left">
                    The environment is be too new. Please reach out on Slack if
                    the problem persists 30 minutes after environment creation.
                  </div>
                </div>
              </div>
            )}
            {fallback && (
              <div className="text-gray-500 text-sm">
                Size:{" "}
                {formatBytes(fallback.current_snapshot_size_bytes || 0, 2)}
              </div>
            )}
            {(!open || (fallback && fallback.clients.length === 0)) && (
              <div className="px-4 pt-4">
                {connectedClients} connected clients.
              </div>
            )}
            <Disclosure.Panel className="px-4 pt-4">
              <table className="flex flex-col space-y-2">
                <tbody>
                  {fallback?.clients
                    .filter(
                      (_, i) => i >= startIdx && i < startIdx + CLIENTS_PER_PAGE
                    )
                    .map((c) => (
                      <tr key={c.client_id} className="hover:bg-gray-50">
                        <td className="py-1">{c.client_id}</td>
                        <td className="py-1 pl-4">
                          <FallbackClientHealthBadge
                            snapshot_timestamp={c.snapshot_timestamp}
                            last_seen_at={c.last_seen_at}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {fallback && fallback.clients.length > 0 && (
                <div className="mt-2 flex items-center">
                  <Button
                    xsmall
                    icon={<HiArrowLeft />}
                    disabled={startIdx === 0}
                    onClick={() => setStartIdx((idx) => idx - 1)}
                  />
                  <span className="px-2">{startIdx + 1}</span>
                  <Button
                    xsmall
                    icon={<HiArrowRight />}
                    disabled={
                      startIdx ===
                      Math.ceil(fallback.clients.length / CLIENTS_PER_PAGE) - 1
                    }
                    onClick={() => setStartIdx((idx) => idx + 1)}
                  />
                </div>
              )}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
};

const FallbackManagementView: React.FC<{}> = ({}) => {
  const { tenant } = useTenant();

  const { data: fallbackEnvs, refresh: refreshFallback } = useData<
    FallbackEnvironment[]
  >(() => (tenant ? `/web/tenants/${tenant.id}/fallback/status` : null));

  useEffect(() => {
    refreshFallback();
  }, [tenant]);

  useEffect(() => {
    const interval = setInterval(() => refreshFallback(), 60000);
    return () => clearInterval(interval);
  }, [refreshFallback]);

  const environments = tenant?.environments.map((e) => {
    return {
      ...e,
      fallback: fallbackEnvs?.find((f) => e.pub_id === f.environment_pub_id),
    };
  });

  if (tenant === null || fallbackEnvs === undefined) {
    return <div></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-space mb-2">Fallback</h2>
      <div className="text-sm">
        Oso Fallback are nodes deployed to your infrastructure. They act as a
        contingency if Oso Cloud is unreachable for any reason.
      </div>
      <ProductionOnlySettings
        youCannot="enable Fallback for environments in"
        youWillBeAbleTo="deploy Fallback nodes to your infrastructure"
        utmContent="fallback"
      >
        {environments?.map((env) => (
          <FallbackManagementEnvironment
            key={env.pub_id}
            env={env}
            fallback={env.fallback}
            className="mt-6"
          />
        ))}
      </ProductionOnlySettings>
    </div>
  );
};

export default FallbackManagementView;
